import { createSlice } from '@reduxjs/toolkit'

import type { ESearchType } from '@/pages/RunReport/types'

export interface RunReportSliceState {
  useCases: {
    selectedUseCaseId: string
    selectedUseCaseName: string
    consentSwitchValue: boolean
    isSwitchDisabled: boolean
  }
  consent: {
    method: string
    consentEmail: string
    file: string
    fileName: string
    subjectLocationValue: string
    subjectLocationName: string
    businessLocationValue: string
    businessLocationName: string
  }
  selectSearchType: {
    reportType: string
    searchType: ESearchType | null
  }
  reportSetup: {
    pdfCoreFindings: boolean
    pdfFlaggedBehaviors: boolean
    pdfLanguage: string
    pdfNegativePosts: boolean
    pdfNeutralPosts: boolean
    pdfPositivePosts: boolean
    pdfSentimentAnalysis: boolean
    pdfSocialMediaAccounts: boolean
    pdfWebSearchPosts: boolean
    useDefaultValue: boolean
    pdfPersonalityAndSkills: boolean
    pdfEducationAndCareer: boolean
    pdfDigitalFootprint: boolean
    pdfContentRadar: boolean
    pdfUnverifiedDigitalFootprint: boolean
  }
  firstStep: {
    topics: string[]
  }
  subjectInfo: {
    firstName: string
    lastName: string
    email: string
    middleName: string
    alias: string
    phoneNumber: string
    college: string
    employerName: string
    description: string
    dob: string
    countryLongName: string
    countryShortName: string
    stateLongName: string
    stateShortName: string
    cityLongName: string
    cityShortName: string
    image: File | string | null
  }
  socialMedia: {
    facebook: string
    instagram: string
    x: string
    linkedIn: string
    tikTok: string
    pinterest: string
    reddit: string
  }
  companyInfo: {
    name: string
    website: string
    industry: string
    description: string
    countryLongName: string
    countryShortName: string
    stateLongName: string
    stateShortName: string
    cityLongName: string
    cityShortName: string
  }
  searchResultsInfo: {
    firstName: string
    lastName: string
    school: string
    company: string
    linkedInUrl: string
    searchInput: string
  }
  isPrefillForm: boolean
}

export const runReportInitialState: RunReportSliceState = {
  useCases: {
    selectedUseCaseId: '',
    selectedUseCaseName: '',
    consentSwitchValue: false,
    isSwitchDisabled: false,
  },
  consent: {
    method: '',
    consentEmail: '',
    file: '',
    fileName: '',
    subjectLocationValue: '',
    subjectLocationName: '',
    businessLocationValue: '',
    businessLocationName: '',
  },
  selectSearchType: {
    reportType: '',
    searchType: null,
  },
  firstStep: {
    topics: [],
  },
  reportSetup: {
    pdfCoreFindings: false,
    pdfFlaggedBehaviors: false,
    pdfLanguage: 'en',
    pdfNegativePosts: false,
    pdfNeutralPosts: false,
    pdfPositivePosts: false,
    pdfSentimentAnalysis: false,
    pdfSocialMediaAccounts: false,
    pdfWebSearchPosts: false,
    useDefaultValue: true,
    pdfPersonalityAndSkills: false,
    pdfEducationAndCareer: false,
    pdfDigitalFootprint: false,
    pdfContentRadar: false,
    pdfUnverifiedDigitalFootprint: false,
  },
  subjectInfo: {
    firstName: '',
    lastName: '',
    email: '',
    middleName: '',
    alias: '',
    phoneNumber: '',
    college: '',
    dob: '',
    countryLongName: '',
    countryShortName: '',
    stateLongName: '',
    stateShortName: '',
    cityLongName: '',
    cityShortName: '',
    employerName: '',
    description: '',
    image: '',
  },
  socialMedia: {
    facebook: '',
    instagram: '',
    x: '',
    linkedIn: '',
    tikTok: '',
    reddit: '',
    pinterest: '',
  },
  companyInfo: {
    name: '',
    website: '',
    industry: '',
    description: '',
    countryLongName: '',
    countryShortName: '',
    stateLongName: '',
    stateShortName: '',
    cityLongName: '',
    cityShortName: '',
  },
  searchResultsInfo: {
    firstName: '',
    lastName: '',
    school: '',
    company: '',
    linkedInUrl: '',
    searchInput: '',
  },
  isPrefillForm: false,
}

const runReportSlice = createSlice({
  name: 'runReport',
  initialState: runReportInitialState,
  reducers: {
    setReportType: (state, action) => {
      state.selectSearchType.reportType = action.payload.reportType
    },
    setSearchType: (state, action) => {
      state.selectSearchType.searchType = action.payload
    },
    setUseCases: (state, action) => {
      const useCases = action.payload
      state.useCases = { ...useCases }
    },
    setConsent: (state, action) => {
      state.consent = {
        ...runReportInitialState.consent,
        ...action.payload,
      }
    },
    resetConsent: (state) => {
      state.consent = runReportInitialState.consent
    },
    setReportSetup: (state, action) => {
      const reportSetup = action.payload
      state.reportSetup = { ...reportSetup }
    },
    changeReportLang: (state, action) => {
      const reportLang = action.payload
      state.reportSetup.pdfLanguage = reportLang
    },
    setFirstStep: (state, action) => {
      const { topics } = action.payload
      state.firstStep.topics = topics
    },
    setSubjectInfo: (state, action) => {
      state.subjectInfo = {
        ...runReportInitialState.subjectInfo,
        ...action.payload,
      }
    },
    clearSubjectInfo: (state) => {
      state.subjectInfo = { ...runReportInitialState.subjectInfo }
    },
    setSocialMedia: (state, action) => {
      state.socialMedia = {
        ...runReportInitialState.socialMedia,
        ...action.payload,
      }
    },
    clearSocialMedia: (state) => {
      state.socialMedia = { ...runReportInitialState.socialMedia }
    },
    setCompanyInfo: (state, action) => {
      state.companyInfo = {
        ...runReportInitialState.companyInfo,
        ...action.payload,
      }
    },
    clearNewSearch: (state) => {
      state = { ...runReportInitialState }
      return state
    },
    setSearchResultsInfo: (state, action) => {
      const searchResultsInfo = action.payload
      state.searchResultsInfo = { ...searchResultsInfo }
    },
    setIsPrefillForm: (state, action) => {
      const isPrefillForm = action.payload
      state.isPrefillForm = isPrefillForm
    },
  },
})

export const {
  setUseCases,
  setConsent,
  setReportType,
  setSearchType,
  setReportSetup,
  setFirstStep,
  clearNewSearch,
  setSocialMedia,
  clearSocialMedia,
  resetConsent,
  setSubjectInfo,
  clearSubjectInfo,
  changeReportLang,
  setCompanyInfo,
  setSearchResultsInfo,
  setIsPrefillForm,
} = runReportSlice.actions

export default runReportSlice.reducer
